import type { MutableRefObject } from 'react';

import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

export type ConnectorSourceEntry = {
	key: string;
	display: string;
};

export type ConnectorSourceInputRuleConfig = {
	knownSourcesRef: MutableRefObject<ConnectorSourceEntry[]>;
};

/**
 * Given the text before the cursor, try to find a connector source name
 * at the end. Matching is case-insensitive against `entry.key`.
 * Returns the original `display` name and the start offset, or null.
 */
export const findTrailingSource = (
	textBefore: string,
	sources: ConnectorSourceEntry[],
): { name: string; startOffset: number } | null => {
	const lower = textBefore.toLowerCase();
	for (const { key, display } of sources) {
		if (lower.endsWith(key)) {
			const startOffset = textBefore.length - key.length;
			if (startOffset === 0 || /\s/.test(textBefore[startOffset - 1])) {
				return { name: display, startOffset };
			}
		}
	}
	return null;
};

export const connectorSourceInputRulePlugin: NextEditorPlugin<
	'connectorSourceInputRule',
	{
		pluginConfiguration: ConnectorSourceInputRuleConfig;
	}
> = ({ config: { knownSourcesRef } }) => {
	return {
		name: 'connectorSourceInputRule',
		pmPlugins() {
			return [
				{
					name: 'connectorSourceInputRule',
					plugin: ({ schema }) => {
						return new SafePlugin({
							props: {
								handleTextInput(view: EditorView, from: number, to: number, text: string): boolean {
									if (text !== ' ') {
										return false;
									}

									const sourceNames = knownSourcesRef.current;
									if (!sourceNames.length) {
										return false;
									}

									const { state } = view;
									const { $from } = state.selection;
									const textBefore =
										$from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc') + '';

									const match = findTrailingSource(textBefore, sourceNames);
									if (!match) {
										return false;
									}

									const codeMark = schema.marks.code;
									if (!codeMark) {
										return false;
									}

									const absoluteStart = $from.start() + match.startOffset;
									const absoluteEnd = from;

									const codeTextNode = schema.text(match.name, [codeMark.create()]);

									const tr = state.tr.replaceWith(absoluteStart, absoluteEnd, [
										codeTextNode,
										schema.text(' '),
									]);

									view.dispatch(tr);
									return true;
								},
							},
						});
					},
				},
			];
		},
	};
};
